import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Form } from "@unform/web";
import { Asterisk, ClipboardList, History as HistoryIcon, ShieldCheck } from "lucide-react";
import { parseISO } from "date-fns";
import { toast } from "react-toastify";
import FormHeader from "~/components/RegisterForm/FormHeader";
import FormLoading from "~/components/RegisterForm/FormLoading";
import Input from "~/components/RegisterForm/Input";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import Preview from "~/components/Preview";
import { Attachments, AttachmentGrid, C, MODULES, STATUS, StatusChip, buildFormData, errorMessage, formatDate, formatMinutes, useFileDrop } from "~/components/SupportChat/tickets";
import api from "~/services/api";
import { useTicketUnread } from "~/components/SupportChat/useTicketUnread";
import { FullGridContext } from "../..";
import Actions from "./Actions";
import ApproverSlots, { useApproverSlots } from "./Approvers";
import Defaults from "./Defaults";
import Timeline from "./Timeline";

export const InputContext = createContext({});

const TYPE_OPTIONS = [
  { value: "bug", label: "Bug" },
  { value: "feature", label: "Feature" },
];
const MODULE_OPTIONS = MODULES.map(({ value, label }) => ({ value, label }));
const labelOf = (options, value) => options.find((o) => o.value === value)?.label || value;

const DECISION = {
  approved: { label: "Approved", color: C.green },
  rejected: { label: "Rejected", color: C.red },
  changes_requested: { label: "Changes requested", color: C.amber },
};

function Description({ value, onChange, readOnly, type }) {
  return (
    <div className="flex flex-col justify-center items-start w-full">
      <div className="px-2 text-xs flex items-center">
        Description {!readOnly && <Asterisk color="#e00" size={12} />}
      </div>
      <div className={`border rounded-lg p-2 px-4 text-sm w-full ${readOnly ? "bg-slate-100" : ""}`}>
        <textarea
          value={value}
          readOnly={readOnly}
          rows={9}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={type === "bug" ? "What happened? What did you expect? How can we reproduce it?" : "What should it do? Who needs it and why?"}
          className="text-sm focus:outline-hidden w-full bg-transparent resize-none"
        />
      </div>
    </div>
  );
}

function ApprovalStatus({ ticket }) {
  const { progress, approvers, approver_source: source, approvals, current_version_id: versionId } = ticket;
  const votes = approvals.filter((a) => a.version_id === versionId);
  return (
    <div className="w-full px-4 pb-8 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <div className="flex gap-1">
          {Array.from({ length: progress.required }).map((_, i) => (
            <span key={i} className="w-6 h-6 rounded-full border flex items-center justify-center text-white text-xs" style={{ background: i < progress.approved ? C.green : "#fff", borderColor: i < progress.approved ? C.green : "#d1d5db" }}>
              {i < progress.approved ? "✓" : ""}
            </span>
          ))}
        </div>
        <span className="text-sm font-bold">{progress.approved} of {progress.required} approvals</span>
        <span className="text-xs text-gray-500">Version {progress.version}</span>
      </div>
      <p className="text-xs text-gray-500">
        {source === "ticket" ? "Approvers picked on this ticket." : "No approvers were picked on this ticket, so the default approvers apply."}
      </p>
      <div className="border rounded-lg divide-y">
        {!approvers.length && <div className="p-4 text-xs text-gray-400 text-center">No approvers set.</div>}
        {approvers.map((user) => {
          const vote = votes.find((v) => v.approver_id === user.id);
          const decision = DECISION[user.decision];
          return (
            <div key={user.id} className="px-4 py-3 flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">{user.name}</span>
                <span className="text-[11px] font-bold border rounded-full px-2 py-0.5" style={{ color: decision?.color || C.textMid, borderColor: decision?.color || "#d1d5db" }}>
                  {decision?.label || "Waiting"}
                </span>
              </div>
              {vote?.reason && <span className="text-xs text-gray-600 whitespace-pre-wrap">{vote.reason}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TicketForm({ access, id }) {
  const { handleOpened, successfullyUpdated, setSuccessfullyUpdated } = useContext(FullGridContext);
  const isNew = id === "new";
  const generalForm = useRef();
  const [ticket, setTicket] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [activeMenu, setActiveMenu] = useState("general");
  const [fullscreen, setFullscreen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("bug");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [versionNumber, setVersionNumber] = useState(null);
  const approvers = useApproverSlots();
  const updateFiles = (update) => {
    setFiles(update);
    setSuccessfullyUpdated(false);
  };
  const drop = useFileDrop(updateFiles);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tickets/${id}`);
      setTicket(data);
      setVersionNumber((current) => current ?? data.progress.version);
      useTicketUnread.getState().refresh();
    } catch (e) {
      toast.error(errorMessage(e));
      handleOpened(null);
    }
  }, [id]);

  useEffect(() => {
    setSuccessfullyUpdated(true);
    if (isNew) {
      api.get("/tickets/settings").then(({ data }) => setDefaults(data)).catch((e) => toast.error(errorMessage(e)));
    } else {
      load();
    }
  }, []);

  useEffect(() => {
    if (isNew) return undefined;
    const refresh = () => {
      if (document.visibilityState === "visible") api.get(`/tickets/${id}`).then(({ data }) => setTicket(data)).catch(() => {});
    };
    const timer = setInterval(refresh, 8000);
    window.addEventListener("focus", refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", refresh);
    };
  }, [id]);

  function handleCloseForm() {
    if (!successfullyUpdated) toast("Changes discarted!", { autoClose: 1000 });
    handleOpened(null);
  }

  async function handleSubmit(data) {
    if (!isNew) return;
    if (!data.module) return toast.error("Choose the section where it happens.");
    if (description.trim().length < 3) return toast.error("Describe the ticket.");
    setLoading(true);
    try {
      const { data: created } = await api.post(
        "/tickets",
        buildFormData(
          {
            type: data.type || type,
            module: data.module,
            title: data.title,
            description,
            required_approvals: data.required_approvals,
            approver_ids: JSON.stringify(approvers.ids()),
            page_url: window.location.pathname,
            source: "system",
          },
          files
        )
      );
      toast(`${created.code} created`, { autoClose: 1500 });
      setSuccessfullyUpdated(true);
      handleOpened(null);
      window.dispatchEvent(new CustomEvent("ticket-created", { detail: created.id }));
    } catch (e) {
      toast.error(errorMessage(e));
      setLoading(false);
    }
  }

  const ready = isNew ? !!defaults : !!ticket;
  const version = ticket?.versions.find((v) => v.version_number === versionNumber) || ticket?.versions[ticket.versions.length - 1];
  const versionFiles = ticket
    ? ticket.events.filter((e) => ["created", "version_created"].includes(e.action) && e.version_id === version?.id).flatMap((e) => e.attachments || [])
    : [];
  const defaultNames = defaults?.approvers.map((a) => a.name).join(", ");

  const context = { id, generalForm, setSuccessfullyUpdated, fullscreen, setFullscreen, successfullyUpdated, handleCloseForm, canceled: null };

  return (
    <Preview formType="full" fullscreen={fullscreen}>
      <div className="flex h-full flex-row items-start justify-between gap-4">
        <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
          <RegisterFormMenu setActiveMenu={setActiveMenu} activeMenu={activeMenu} name="general">
            <ClipboardList size={16} /> General
          </RegisterFormMenu>
          <RegisterFormMenu setActiveMenu={setActiveMenu} activeMenu={activeMenu} name="approval">
            <ShieldCheck size={16} /> Approval
          </RegisterFormMenu>
          <RegisterFormMenu setActiveMenu={setActiveMenu} activeMenu={activeMenu} disabled={isNew} messageOnDisabled="Create the ticket first to see its activity" name="history">
            <HistoryIcon size={16} /> Activity
          </RegisterFormMenu>
        </div>

        <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
          <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll" {...(isNew ? drop.handlers : {})}>
            {!ready ? (
              <FormLoading />
            ) : (
              <Form ref={generalForm} onSubmit={handleSubmit} className="w-full">
                <InputContext.Provider value={context}>
                  <FormHeader
                    access={access}
                    title={isNew ? "New ticket" : `${ticket.code} · ${ticket.title}`}
                    registry={isNew ? undefined : { registryBy: ticket.reporter?.name, registryAt: parseISO(ticket.created_at), registryStatus: "Opened" }}
                    InputContext={InputContext}
                    loading={loading}
                    disableSave={!isNew}
                    createText="Create ticket"
                  />

                  <InputLineGroup title="GENERAL" activeMenu={activeMenu === "general"}>
                    {isNew ? (
                      <>
                        <InputLine title="Ticket">
                          <SelectPopover
                            name="type"
                            title="Type"
                            required
                            options={TYPE_OPTIONS}
                            defaultValue={TYPE_OPTIONS[0]}
                            setReturnPopover={(option) => setType(option.value)}
                            InputContext={InputContext}
                          />
                          <SelectPopover name="module" title="Section (where it happens)" grow required options={MODULE_OPTIONS} InputContext={InputContext} />
                        </InputLine>
                        <InputLine title="Summary">
                          <Input type="text" name="title" title="Title" required grow maxLength={200} InputContext={InputContext} />
                        </InputLine>
                        <InputLine title="Details">
                          <Description
                            value={description}
                            onChange={(value) => {
                              setDescription(value);
                              setSuccessfullyUpdated(false);
                            }}
                            type={type}
                          />
                        </InputLine>
                        <InputLine title="Attachments" subtitle="Paste (Ctrl+V) or drag images, videos and PDFs anywhere on this form.">
                          <Attachments files={files} setFiles={updateFiles} />
                        </InputLine>
                      </>
                    ) : (
                      <>
                        <InputLine title="Ticket">
                          <Input type="text" name="code" title="Code" readOnly defaultValue={ticket.code} InputContext={InputContext} />
                          <Input type="text" name="type" title="Type" readOnly defaultValue={labelOf(TYPE_OPTIONS, ticket.type)} InputContext={InputContext} />
                          <Input type="text" name="module" title="Section" readOnly grow defaultValue={labelOf(MODULE_OPTIONS, ticket.module)} InputContext={InputContext} />
                          <Input type="text" name="status" title="Status" readOnly defaultValue={STATUS[ticket.status]?.label || ticket.status} InputContext={InputContext} />
                          {ticket.staff_level > 0 && <Input type="text" name="level" title="Level" readOnly defaultValue={`N${ticket.level}`} InputContext={InputContext} />}
                        </InputLine>
                        <InputLine title="Summary">
                          <Input type="text" name="title" title="Title" readOnly grow defaultValue={ticket.title} InputContext={InputContext} />
                          <Input type="text" name="reporter" title="Opened by" readOnly defaultValue={ticket.reporter?.name} InputContext={InputContext} />
                          <Input type="text" name="branch" title="Branch" readOnly defaultValue={ticket.filial?.name || "-"} InputContext={InputContext} />
                          <Input key={ticket.assignee?.id || "none"} type="text" name="assignee" title="Assigned to" readOnly defaultValue={ticket.assignee?.name || "Unassigned"} InputContext={InputContext} />
                        </InputLine>
                        <InputLine title="Details">
                          <div className="w-full flex flex-col gap-2">
                            {ticket.versions.length > 1 && (
                              <div className="flex flex-wrap items-center gap-2">
                                {ticket.versions.map((v) => (
                                  <button
                                    key={v.id}
                                    type="button"
                                    onClick={() => setVersionNumber(v.version_number)}
                                    className={`text-xs font-bold rounded-full px-3 py-1 border ${v.id === version.id ? "bg-primary text-white border-primary" : "bg-white text-primary border-primary"}`}
                                  >
                                    v{v.version_number}
                                  </button>
                                ))}
                                {ticket.status && <StatusChip status={ticket.status} />}
                              </div>
                            )}
                            {version?.reason && <p className="text-xs italic text-gray-500">{version.reason}</p>}
                            <Description value={version?.description || ""} readOnly />
                            <AttachmentGrid items={versionFiles} />
                          </div>
                        </InputLine>
                        {ticket.closing_note && (
                          <InputLine title="Closing message">
                            <Input type="text" name="resolved_on" title="Resolved on" readOnly defaultValue={formatDate(ticket.closed_at)} InputContext={InputContext} />
                            <Input type="text" name="time_to_resolve" title="Elapsed time" readOnly defaultValue={formatMinutes(ticket.time_spent_minutes)} InputContext={InputContext} />
                            <Input type="text" name="work_time" title="Time spent on the solution" readOnly defaultValue={formatMinutes(ticket.work_minutes)} InputContext={InputContext} />
                            <p className="text-sm whitespace-pre-wrap">{ticket.closing_note}</p>
                          </InputLine>
                        )}
                      </>
                    )}
                  </InputLineGroup>

                  <InputLineGroup title="Approval" activeMenu={activeMenu === "approval"}>
                    {isNew ? (
                      <>
                        <InputLine title="Rule">
                          <Input
                            key={type}
                            type="number"
                            name="required_approvals"
                            title="Approvals needed"
                            min={1}
                            max={10}
                            defaultValue={defaults[`${type}_required_approvals`] ?? 1}
                            InputContext={InputContext}
                          />
                        </InputLine>
                        <InputLine title="Who approves" subtitle="Use the magnifier to search for the people who must approve this ticket." />
                        <ApproverSlots
                          state={approvers}
                          InputContext={InputContext}
                          hint={defaultNames ? `Leave empty to use the default approvers: ${defaultNames}.` : "Leave empty to use the default approvers."}
                        />
                      </>
                    ) : (
                      <>
                        <div className="w-full pb-4">
                          <Actions ticket={ticket} onDone={load} />
                        </div>
                        <ApprovalStatus ticket={ticket} />
                      </>
                    )}
                  </InputLineGroup>

                  {!isNew && (
                    <InputLineGroup title="Activity" activeMenu={activeMenu === "history"}>
                      <Timeline ticket={ticket} onDone={load} />
                    </InputLineGroup>
                  )}
                </InputContext.Provider>
              </Form>
            )}
          </div>
        </div>
      </div>
    </Preview>
  );
}

export default function PagePreview({ access, id }) {
  if (id === "defaults") return <Defaults access={access} />;
  return <TicketForm key={id} access={access} id={id} />;
}
